import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  Info, ShoppingBag, Briefcase, Tag, ExternalLink
} from 'lucide-react';
import { useContent } from '../contexts/ContentContext';

export const InfoButton: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { content, t, isTheatreMode, isMenuOpen, is3DInspectionActive } = useContent();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Only show on project detail pages
  const isProjectPage = location.pathname.startsWith('/projects/') && id;
  const project = isProjectPage ? content.projects.find((p: any) => p.id === id) : null;
  // Hide if in Theatre Mode, Inspection mode, or mobile menu is active
  const shouldShow = !!(isProjectPage && project && !isTheatreMode && !isMenuOpen && !is3DInspectionActive);

  useEffect(() => {
    if (shouldShow) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setIsSidebarOpen(false); // Auto-close sidebar if menu opens or navigating away
    }
  }, [shouldShow]);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  if (!isProjectPage || !project) return null;

  const listServices = typeof t(project.descriptionServices) === 'string' 
    ? (t(project.descriptionServices) as string).split(',').map(s => s.trim()).filter(Boolean)
    : [];

  const findServiceId = (serviceName: string) => {
    const match = content.services.find(s => t(s.title).toLowerCase() === serviceName.toLowerCase());
    return match ? match.id : null;
  };

  const hasLinks = project.links && Object.values(project.links).some(val => !!val);
  const hasServices = listServices.length > 0;
  const hasTags = project.tags && project.tags.length > 0;

  const FieldLabel = ({ label, icon: Icon }: { label: string, icon?: any }) => (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={14} className="text-accent" />}
      <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">{label}</p>
    </div>
  );

  const Chip: React.FC<{ text: string, isLink?: boolean }> = ({ text, isLink = false }) => (
    <span className={`inline-block px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[9px] font-black uppercase tracking-[0.1em] text-gray-300 transition-all hover:bg-accent hover:text-white hover:border-accent ${isLink ? 'cursor-pointer' : ''}`}>
      {text}
    </span>
  );

  return createPortal(
    <>
      {/* DESKTOP TOGGLE TAB */}
      <div 
        className={`fixed left-0 top-1/2 -translate-y-1/2 z-[100] hidden md:block transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${
          isVisible && !isSidebarOpen 
            ? 'translate-x-0 opacity-100' 
            : '-translate-x-full opacity-0 pointer-events-none'
        }`}
      >
        <button 
          onClick={() => setIsSidebarOpen(true)}
          className="group flex items-center"
          aria-label="Open Project Details"
        >
          <div className="bg-accent text-white h-48 w-12 rounded-r-3xl flex flex-col items-center justify-center gap-6 shadow-[10px_0_40px_rgba(249,115,22,0.4)] hover:w-16 transition-all border-y border-r border-white/20 hover:bg-orange-500">
             <Info size={24} className="group-hover:rotate-12 transition-transform" />
             <span className="[writing-mode:vertical-lr] rotate-180 text-[10px] font-black uppercase tracking-[0.4em] whitespace-nowrap">Details</span>
          </div>
        </button>
      </div>

      {/* MOBILE FLOATING BUTTON */}
      <div 
        className={`fixed bottom-8 left-8 z-[100] md:hidden transition-all duration-500 transform ${
          isVisible && !isSidebarOpen 
            ? 'translate-y-0 opacity-100 scale-100' 
            : 'translate-y-10 opacity-0 scale-50 pointer-events-none'
        }`}
      >
        <button 
          onClick={() => setIsSidebarOpen(true)}
          className="w-14 h-14 bg-accent text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(249,115,22,0.5)] active:scale-95 border border-white/20"
        >
          <Info size={22} />
        </button>
      </div>

      {/* OVERLAY & SIDEBAR PANEL */}
      <div 
        className={`fixed inset-0 z-[10000] transition-all duration-700 ease-in-out ${
          isSidebarOpen ? 'bg-black/60 backdrop-blur-[4px] pointer-events-auto opacity-100' : 'bg-transparent pointer-events-none opacity-0'
        }`}
        onClick={() => setIsSidebarOpen(false)}
      >
        <aside 
          className={`fixed top-4 bottom-4 left-4 right-4 md:top-6 md:bottom-6 md:left-6 md:right-auto md:w-full md:max-w-[440px] bg-[#0d0a0f]/95 backdrop-blur-2xl border border-white/10 rounded-[32px] md:rounded-[40px] shadow-[0_40px_120px_rgba(0,0,0,1)] transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col overflow-hidden transform ${
            isSidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-[calc(100%+40px)] opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 md:p-10 flex items-center justify-between border-b border-white/5 bg-white/[0.02] shrink-0">
             <div className="space-y-1">
               <h3 className="text-xl md:text-2xl font-gaming font-bold text-white uppercase tracking-tight leading-none">
                 {t(project.title)}
               </h3>
             </div>
          </div>

          {/* Scrollable Body */}
          <div className="flex-grow overflow-y-auto custom-scrollbar p-6 md:p-10 space-y-10 md:space-y-12">
            
            {/* Links */}
            {hasLinks && (
              <div className={`space-y-6 transition-all duration-[900ms] delay-[150ms] transform ${isSidebarOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <FieldLabel label="Links" icon={ShoppingBag} />
                <div className="space-y-3">
                  {Object.entries(project.links!).map(([key, val]) => (
                    val ? (
                      <a 
                        key={key} 
                        href={val as string} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 md:p-5 bg-white/[0.03] border border-white/5 rounded-2xl hover:border-accent hover:bg-accent/[0.07] transition-all group overflow-hidden relative shadow-lg"
                      >
                        <div className="absolute inset-0 bg-grid opacity-0 group-hover:opacity-10 transition-opacity"></div>
                        <span className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.3em] text-gray-400 group-hover:text-white transition-colors relative z-10">{key} Portal</span>
                        <ExternalLink size={14} className="text-accent group-hover:scale-110 transition-transform relative z-10" />
                      </a>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* Production services */}
            {hasServices && (
              <div className={`space-y-6 transition-all duration-[900ms] delay-[250ms] transform ${isSidebarOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <FieldLabel label="Production services" icon={Briefcase} />
                <div className="flex flex-wrap gap-2">
                  {listServices.map((serviceName, i) => {
                    const serviceId = findServiceId(serviceName);
                    if (serviceId) {
                      return (
                        <Link key={i} to={`/services/${serviceId}`} onClick={() => setIsSidebarOpen(false)}>
                          <Chip text={serviceName} isLink />
                        </Link>
                      );
                    }
                    return <Chip key={i} text={serviceName} />;
                  })}
                </div>
              </div>
            )}

            {/* Tags */}
            {hasTags && (
              <div className={`space-y-6 pb-8 md:pb-12 transition-all duration-[900ms] delay-[350ms] transform ${isSidebarOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <FieldLabel label="Tags" icon={Tag} />
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag: string) => <Chip key={tag} text={tag} />)}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 md:p-10 border-t border-white/5 bg-black/40 backdrop-blur-xl shrink-0">
             <button onClick={() => setIsSidebarOpen(false)} className="btn-primary w-full py-4 md:py-5 text-[10px] shadow-[0_0_30px_rgba(249,115,22,0.2)] rounded-2xl md:rounded-3xl uppercase font-black tracking-widest">Return to project</button>
          </div>
        </aside>
      </div>
    </>,
    document.body
  );
};